import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Edit, CheckSquare, Mail } from 'lucide-react';

const DoctorAccountsPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Doctor RCTI';
  }, []);

  // State for RCTI data
  const [doctorId, setDoctorId] = useState('Doctor ID Number');
  const [doctorName, setDoctorName] = useState('Doctor Name');
  const [periodCommencing, setPeriodCommencing] = useState('2025-02-01');
  const [periodEnding, setPeriodEnding] = useState('2025-02-07');
  const [loading, setLoading] = useState(false);

  // Sample RCTI data matching the uploaded image
  const rctiData = [
    {
      day: 'Monday',
      date: '01/02/2025',
      appointmentsCompleted: 25,
      editableUnits: 25,
      unitRate: '$50.00',
      totalExGst: '$1,250.00',
      gst: '$125.00',
      totalIncGst: '$1,375.00',
      editable: true,
      checked: false
    },
    {
      day: 'Tuesday',
      date: '02/02/2025',
      appointmentsCompleted: 25,
      editableUnits: 25,
      unitRate: '$50.00',
      totalExGst: '$1,250.00',
      gst: '$125.00',
      totalIncGst: '$1,375.00',
      editable: true,
      checked: false
    },
    {
      day: 'Wednesday',
      date: '03/02/2025',
      appointmentsCompleted: 25,
      editableUnits: 25,
      unitRate: '$50.00',
      totalExGst: '$1,250.00',
      gst: '$125.00',
      totalIncGst: '$1,375.00',
      editable: true,
      checked: false
    },
    {
      day: 'Thursday',
      date: '04/02/2025',
      appointmentsCompleted: 25,
      editableUnits: 25,
      unitRate: '$50.00',
      totalExGst: '$1,250.00',
      gst: '$125.00',
      totalIncGst: '$1,375.00',
      editable: true,
      checked: false
    },
    {
      day: 'Friday',
      date: '05/02/2025',
      appointmentsCompleted: 25,
      editableUnits: 25,
      unitRate: '$50.00',
      totalExGst: '$1,250.00',
      gst: '$125.00',
      totalIncGst: '$1,375.00',
      editable: true,
      checked: false
    },
    {
      day: 'Saturday',
      date: '06/02/2025',
      appointmentsCompleted: null,
      editableUnits: null,
      unitRate: null,
      totalExGst: null,
      gst: null,
      totalIncGst: null,
      editable: true,
      checked: false
    },
    {
      day: 'Sunday',
      date: '07/02/2025',
      appointmentsCompleted: null,
      editableUnits: null,
      unitRate: null,
      totalExGst: null,
      gst: null,
      totalIncGst: null,
      editable: true,
      checked: false
    }
  ];

  const [tableData, setTableData] = useState(rctiData);

  // Calculate totals
  const totals = tableData.reduce((acc, row) => {
    if (row.appointmentsCompleted) {
      acc.totalExGst += parseFloat(row.totalExGst.replace(/[$,]/g, ''));
      acc.gst += parseFloat(row.gst.replace(/[$,]/g, ''));
      acc.totalIncGst += parseFloat(row.totalIncGst.replace(/[$,]/g, ''));
    }
    return acc;
  }, { totalExGst: 0, gst: 0, totalIncGst: 0 });

  const handleEdit = (index) => {
    console.log('Edit row:', index);
    // Handle edit functionality
  };

  const handleCheck = (index) => {
    const updatedData = [...tableData];
    updatedData[index].checked = !updatedData[index].checked;
    setTableData(updatedData);
  };

  const handleSubmit = () => {
    console.log('Submit RCTI');
    // Handle submit functionality
  };

  const handleEmail = () => {
    console.log('Email RCTI');
    // Handle email functionality
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Accounts</h1>
      </div>

      <div className="flex gap-6">
        {/* Left Column - Navigation */}
        <div className="w-[20%]">
          <div className="bg-white rounded-lg shadow-md">
            {/* Navigation Options */}
            <div className="p-4 space-y-3">
              <Link 
                to="/doctor/accounts/rcti"
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-blue-700 hover:scale-105 transform transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-xl"
              >
                <span>RCTI</span>
              </Link>
              
              <Link 
                to="/doctor/accounts"
                className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg text-sm font-medium hover:bg-gray-200 hover:scale-105 transform transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-lg"
              >
                <span>Accounts</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column - RCTI Content */}
        <div className="w-[80%]">
          <div className="bg-white rounded-lg shadow-md">
            {/* RCTI Header */}
            <div className="bg-white px-4 py-2 rounded-t-lg border-b-2 border-blue-500">
              <h3 className="font-bold text-xl text-blue-600 text-center">RCTI</h3>
            </div>

            <div className="p-6">
              {/* Invoice Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-center">
                <p className="text-sm text-blue-800">
                  Invoices must be submitted to email.payroll.com.au by 9:00am AEST on the 1st Monday following the billing cycle.
                </p>
              </div>

              {/* Doctor Information */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Doctor ID</label>
                  <input
                    type="text"
                    value={doctorId}
                    onChange={(e) => setDoctorId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Doctor Name</label>
                  <input
                    type="text"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period Commencing</label>
                  <input
                    type="date"
                    value={periodCommencing}
                    onChange={(e) => setPeriodCommencing(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-400 transition-colors cursor-pointer"
                    placeholder="Select start date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period Ending</label>
                  <input
                    type="date"
                    value={periodEnding}
                    onChange={(e) => setPeriodEnding(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-400 transition-colors cursor-pointer"
                    placeholder="Select end date"
                  />
                </div>
              </div>

              {/* RCTI Table */}
              <div>
                <table className="w-full border-collapse border border-gray-300 text-xs">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-700">Day</th>
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-700">Date</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">Appts</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">Units</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">$/Unit</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">Total ex GST</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">GST</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">Total inc GST</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">Edit</th>
                      <th className="border border-gray-300 px-1 py-1 text-center text-xs font-medium text-gray-700">✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-2 py-1 text-xs font-medium text-gray-900 text-center">{row.day}</td>
                        <td className="border border-gray-300 px-2 py-1 text-xs text-gray-700 text-center">{row.date}</td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.appointmentsCompleted || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.editableUnits || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.unitRate || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.totalExGst || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.gst || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-xs text-gray-700 text-center">
                          {row.totalIncGst || ''}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-center">
                          <button
                            onClick={() => handleEdit(index)}
                            className="bg-blue-100 text-blue-600 px-1 py-0.5 rounded text-xs hover:bg-blue-200 hover:scale-110 transform transition-all duration-150 shadow-sm hover:shadow-md"
                          >
                            Edit
                          </button>
                        </td>
                        <td className="border border-gray-300 px-1 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={row.checked}
                            onChange={() => handleCheck(index)}
                            className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-blue-100 font-medium">
                      <td className="border border-gray-300 px-2 py-1 text-xs text-center" colSpan="5">
                        <span className="font-semibold">Total</span>
                      </td>
                      <td className="border border-gray-300 px-1 py-1 text-xs text-center">
                        ${totals.totalExGst.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-300 px-1 py-1 text-xs text-center">
                        ${totals.gst.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-300 px-1 py-1 text-xs text-center">
                        ${totals.totalIncGst.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-300 px-1 py-1 text-center" colSpan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={handleEmail}
                  className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 hover:scale-105 transform transition-all duration-200 shadow-sm hover:shadow-lg flex items-center space-x-2"
                >
                  <Mail className="h-4 w-4" />
                  <span>Email</span>
                </button>
                <button
                  onClick={handleSubmit}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 hover:scale-105 transform transition-all duration-200 shadow-md hover:shadow-xl"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAccountsPage;
